import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { Save, Loader2, ArrowLeft, Pin, Pencil, ImageIcon, Eye, Sparkles } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ['Wiki', 'Birimler', 'Duyurular', 'Olay'];

function Badge({ children, variant = 'default' }) {
  const map = {
    edited: 'bg-amber-100 text-amber-800 border border-amber-200',
    pinned: 'bg-rose-100 text-rose-800 border border-rose-200',
    default: 'bg-zinc-100 text-zinc-700 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700',
  };
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md inline-flex items-center gap-1 ${map[variant]}`}>{children}</span>;
}

export default function WikiEditor() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const isEdit = !!slug;
  const { user, authHeaders, loading: authLoading } = useAuth();
  const [form, setForm] = useState({ title: '', excerpt: '', body: '', category: 'Wiki', image: '', pinned: false });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('edit');

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API}/wiki/${slug}`).then(r => {
        setForm({
          title: r.data.title || '', excerpt: r.data.excerpt || '', body: r.data.body || '',
          category: r.data.category || 'Wiki', image: r.data.image || '', pinned: !!r.data.pinned,
        });
      });
    }
  }, [slug, isEdit]);

  useEffect(() => {
    if (!authLoading && (!user || !user.canEdit)) navigate('/wiki');
  }, [user, authLoading, navigate]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setErr('Başlık gerekli'); return; }
    setSaving(true); setErr('');
    try {
      if (isEdit) {
        await axios.patch(`${API}/wiki/${slug}`, form, { headers: authHeaders });
        navigate(`/wiki/${slug}`);
      } else {
        const r = await axios.post(`${API}/wiki`, form, { headers: authHeaders });
        navigate(`/wiki/${r.data.slug}`);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Kaydetme başarısız');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <Header />
      <main className="max-w-6xl mx-auto px-5 lg:px-8 pt-8 pb-24">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => navigate(-1)} className="pill-btn text-sm"><ArrowLeft size={14} /> Geri</button>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-full p-1" style={{ background: 'var(--pill-hover)' }}>
              <button onClick={() => setTab('edit')} className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${tab === 'edit' ? 'shadow-sm' : 'opacity-60'}`} style={tab === 'edit' ? { background: 'var(--bg-soft)' } : {}}>
                <Pencil size={13} /> Düzenle
              </button>
              <button onClick={() => setTab('preview')} className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${tab === 'preview' ? 'shadow-sm' : 'opacity-60'}`} style={tab === 'preview' ? { background: 'var(--bg-soft)' } : {}}>
                <Eye size={13} /> Önizleme
              </button>
            </div>
            <button onClick={submit} disabled={saving} className="pill-btn" style={{ background: 'var(--text)', color: 'var(--bg)', borderColor: 'transparent' }}>
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              <span className="font-semibold">{isEdit ? 'Güncelle' : 'Yayınla'}</span>
            </button>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4cc9f0, #7aa7ff)' }}>
            <Sparkles size={16} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">{isEdit ? 'Wiki Yazısını Düzenle' : 'Yeni Wiki Yazısı'}</h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Yazınız <strong>anında yayına alınır</strong>. Rank 90+ yetkinizle herkese açık hale gelir.</p>
          </div>
        </div>

        {err && (
          <div className="mt-4 rounded-xl px-4 py-3 text-sm" style={{ background: 'rgba(244,63,94,0.12)', color: '#f43f5e', border: '1px solid rgba(244,63,94,0.25)' }}>
            {err}
          </div>
        )}

        {tab === 'edit' ? (
          <form onSubmit={submit} className="mt-6 grid lg:grid-cols-3 gap-6">
            {/* Main */}
            <div className="lg:col-span-2 flex flex-col gap-5">
              <div className="card-soft rounded-2xl p-5 lg:p-6">
                <label className="editor-label">Başlık</label>
                <input
                  required
                  autoFocus
                  value={form.title}
                  onChange={e => set('title', e.target.value)}
                  className="mt-2 w-full bg-transparent outline-none text-2xl lg:text-3xl font-semibold tracking-tight"
                  placeholder="Yazının başlığı..."
                />
                <div className="mt-4 h-px" style={{ background: 'var(--border)' }} />
                <label className="editor-label mt-4 block">Özet</label>
                <input
                  value={form.excerpt}
                  onChange={e => set('excerpt', e.target.value)}
                  className="mt-2 w-full bg-transparent outline-none text-base"
                  placeholder="Yazının kısa açıklaması (listede görünür)..."
                />
              </div>

              <div className="card-soft rounded-2xl p-5 lg:p-6">
                <label className="editor-label">İçerik</label>
                <textarea
                  required
                  value={form.body}
                  onChange={e => set('body', e.target.value)}
                  rows={18}
                  className="mt-2 w-full bg-transparent outline-none text-[15px] leading-relaxed resize-none font-mono"
                  placeholder="Yazınızı buraya yazın..."
                />
                <div className="mt-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                  {form.body.length} karakter · Satır sonları ve boşluklar korunur
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="flex flex-col gap-5">
              <div className="card-soft rounded-2xl p-5">
                <label className="editor-label">Kategori</label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="mt-2 w-full text-left pill-btn justify-between">
                      <span>{form.category}</span>
                      <span style={{ color: 'var(--text-muted)' }}>▾</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {CATS.map(c => <DropdownMenuItem key={c} onClick={() => set('category', c)} className="cursor-pointer">{c}</DropdownMenuItem>)}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="card-soft rounded-2xl p-5">
                <label className="editor-label flex items-center gap-1.5"><ImageIcon size={12} /> Kapak Görseli</label>
                <input
                  value={form.image}
                  onChange={e => set('image', e.target.value)}
                  className="to-input w-full mt-2 text-sm"
                  placeholder="https://..."
                />
                {form.image && (
                  <div className="mt-3 rounded-xl overflow-hidden aspect-[21/9]" style={{ background: 'var(--pill-hover)' }}>
                    <img src={form.image} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>

              <div className="card-soft rounded-2xl p-5">
                <label className="editor-label flex items-center gap-1.5"><Pin size={12} /> Sabitleme</label>
                <label className="mt-3 flex items-center justify-between gap-3 cursor-pointer">
                  <span className="text-sm">Bu yazıyı en üste sabitle</span>
                  <span className={`relative w-10 h-6 rounded-full transition-colors ${form.pinned ? 'bg-emerald-500' : ''}`} style={!form.pinned ? { background: 'var(--pill-hover)' } : {}}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${form.pinned ? 'left-4' : 'left-0.5'}`} />
                    <input type="checkbox" checked={form.pinned} onChange={e => set('pinned', e.target.checked)} className="sr-only" />
                  </span>
                </label>
              </div>

              <div className="card-soft rounded-2xl p-5">
                <label className="editor-label">Yazar</label>
                <div className="mt-3 flex items-center gap-3">
                  {user?.avatar ? <img src={user.avatar} alt="" className="w-10 h-10 rounded-full" /> : <div className="w-10 h-10 rounded-full" style={{ background: 'var(--pill-hover)' }} />}
                  <div>
                    <div className="text-sm font-semibold">{user?.displayName || user?.name}</div>
                    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{user?.rankName} · Rank {user?.rank}</div>
                  </div>
                </div>
              </div>
            </aside>
          </form>
        ) : (
          <article className="mt-6 card-soft rounded-3xl overflow-hidden">
            {form.image && (
              <div className="aspect-[21/8] overflow-hidden" style={{ background: 'var(--pill-hover)' }}>
                <img src={form.image} alt={form.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-7 lg:p-10">
              <div className="flex items-center gap-2 flex-wrap">
                {form.pinned && <Badge variant="pinned"><Pin size={10} /> Pinned</Badge>}
                <Badge>{form.category}</Badge>
              </div>
              <h1 className="mt-3 text-3xl lg:text-4xl font-semibold tracking-tight">{form.title || 'Başlıksız'}</h1>
              <div className="mt-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                <span className="font-semibold" style={{ color: 'var(--text)' }}>{user?.name}</span> — {user?.rankName}
              </div>
              {form.excerpt && <p className="mt-6 text-lg leading-relaxed font-medium">{form.excerpt}</p>}
              <div className="mt-4 whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                {form.body || 'İçerik önizlemesi burada görünecek...'}
              </div>
            </div>
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}
